import {writable, get} from 'svelte/store';
import {
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  type User
} from 'firebase/auth';
import {auth as firebaseAuth} from '../utils/firebase';

import {CLIENT_ID, SCOPE} from '../configs/google';

export const user = writable<User | null>(null);

export const tokenClient = writable<
  google.accounts.oauth2.TokenClient | undefined
>();

function createAuthStore() {
  const {subscribe} = writable({
    user,
    tokenClient
  });

  let accessToken: string | null | undefined;

  // Save logged in user and keep user signed in on page refresh
  onAuthStateChanged(firebaseAuth, loggedInUser => {
    user.set(loggedInUser);
  });

  // Refresh access token and handle Firebase login
  const handleRequestAccessToken = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const client = get(tokenClient);

      if (!client) {
        handleLogout();
        reject();
        return;
      }

      // @ts-ignore: callback is defined at request time (see documentation https://developers.google.com/identity/oauth2/web/guides/migration-to-gis?hl=en#gapi-callback)
      client.callback = (
        tokenResponse: google.accounts.oauth2.TokenResponse
      ) => {
        accessToken = tokenResponse.access_token;

        if (!accessToken) {
          handleLogout();
          reject();
          return;
        }

        // Sign in Google user to Firebase
        const credential = GoogleAuthProvider.credential(null, accessToken);
        signInWithCredential(firebaseAuth, credential)
          .then(() => {
            resolve();
          })
          .catch(error => {
            console.error(error);
            handleLogout();
            reject();
          });
      };

      const {email} = get(user) || {};
      client.requestAccessToken({
        prompt: '', // The user will be prompted only the first time your app requests access.
        ...(email && {hint: email}) // Pass user email to skip account selection
      });
    });
  };

  // Revoke access token and log out from Firebase
  const handleLogout = () => {
    accessToken = null;
    gapi.client.setToken(null);

    if (get(user)) {
      signOut(firebaseAuth).then(() => {
        user.set(null);
      });
    }
  };

  return {
    subscribe,
    initialize: () => {
      tokenClient.set(
        google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPE,
          prompt: '', // The user will be prompted only the first time your app requests access.
          // @ts-ignore: callback is defined at request time (see documentation https://developers.google.com/identity/oauth2/web/guides/migration-to-gis?hl=en#gapi-callback)
          callback: ''
        })
      );
    },
    requestAccessToken: handleRequestAccessToken,
    login: handleRequestAccessToken,
    logout: handleLogout
  };
}

export const auth = createAuthStore();
